import React from 'react';
import {
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Button,
  Box,
  Typography,
  Alert,
} from '@mui/material';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import type { WorkflowInstance } from './useWorkflowList';
import { STATE_STEPS } from '@/resources/workflowTypeMeta';
import { TYPE_LABELS } from '@/resources/workflowTypeMeta';

interface WorkflowCardProps {
  workflow: WorkflowInstance;
  onOpen?: (workflow: WorkflowInstance) => void;
  onAdvance?: (workflowId: string, event: string) => void;
  onPause?: (workflowId: string) => void;
  onResume?: (workflowId: string) => void;
  onCancel?: (workflowId: string) => void;
  showActions?: boolean;
}

const STATUS_COLORS: Record<string, 'primary' | 'warning' | 'success' | 'default'> = {
  active: 'primary',
  paused: 'warning',
  completed: 'success',
  cancelled: 'default',
};

function formatDate(dateString: string | undefined | null): string {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-ZA', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function WorkflowCard({
  workflow,
  onOpen,
  onAdvance,
  onPause,
  onResume,
  onCancel,
  showActions = true,
}: WorkflowCardProps) {
  const steps = STATE_STEPS[workflow.workflowType] || [];
  const expectedStates = steps.length || 5;
  const transitionCount = workflow.transitions?.length ?? 0;
  const progress = Math.min((transitionCount / expectedStates) * 100, 100);

  const canPause = workflow.status === 'active';
  const canResume = workflow.status === 'paused';
  const canCancelAction = workflow.status === 'active' || workflow.status === 'paused';
  const isViewOnly = workflow.status === 'completed' || workflow.status === 'cancelled';

  const handlePause = () => {
    onPause?.(workflow.id);
  };

  const handleResume = () => {
    onResume?.(workflow.id);
  };

  const handleCancel = () => {
    const reason = prompt('Please provide a reason for cancellation:');
    if (reason) {
      onCancel?.(workflow.id);
    }
  };

  return (
    <Card
      sx={{
        cursor: onOpen ? 'pointer' : 'default',
        transition: 'box-shadow 0.2s',
        '&:hover': onOpen ? {
          boxShadow: 4,
        } : undefined,
      }}
      onClick={() => onOpen?.(workflow)}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: 'bold',
              fontSize: '1rem',
              flexShrink: 0,
            }}
          >
            {workflow.workflowType.charAt(0).toUpperCase()}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle1" fontWeight="bold" noWrap>
              {workflow.entityName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {TYPE_LABELS[workflow.workflowType] || workflow.workflowType}
            </Typography>
          </Box>

          <Chip
            label={workflow.status.charAt(0).toUpperCase() + workflow.status.slice(1)}
            color={STATUS_COLORS[workflow.status]}
            size="small"
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Current State
            </Typography>
            <Typography variant="body2" fontWeight="medium">
              {workflow.currentState.value}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey[200]',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
              },
            }}
          />
        </Box>

        {workflow.status === 'paused' && workflow.breakpointId && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Workflow paused at breakpoint: {workflow.currentState.value}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Started
            </Typography>
            <Typography variant="body2">
              {formatDate(workflow.startedAt)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Last Action
            </Typography>
            <Typography variant="body2">
              {formatDate(workflow.lastActionAt)}
            </Typography>
          </Box>
        </Box>

        {showActions && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {canPause && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PauseIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePause();
                }}
              >
                Pause
              </Button>
            )}
            {canResume && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<PlayArrowIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleResume();
                }}
              >
                Resume
              </Button>
            )}
            {canCancelAction && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleCancel();
                }}
              >
                Cancel
              </Button>
            )}
          </Box>
        )}

        {!showActions && (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Chip
              label={`${transitionCount} transitions`}
              size="small"
              variant="outlined"
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
}